/**
 * TranscriptController — manages live audio transcription segments.
 *
 * Lit Reactive Controller equivalent of useRinggTranscripts from React Native.
 */

import type { ReactiveController, ReactiveControllerHost } from "lit";

import type { RealtimeRoomAdapter, ConnectionState, ChatMessage, TranscriptionSegment } from "@ringg/core";

export class TranscriptController implements ReactiveController {
  host: ReactiveControllerHost;

  private adapter: RealtimeRoomAdapter;
  private cleanupFns: Array<() => void> = [];
  private connState: ConnectionState = "disconnected";
  private segmentMap = new Map<string, ChatMessage>();
  private transcriptionRegistered = false;

  transcripts: ChatMessage[] = [];

  constructor(host: ReactiveControllerHost, adapter: RealtimeRoomAdapter) {
    this.host = host;
    this.adapter = adapter;
    host.addController(this);
  }

  hostConnected(): void {
    const unsub = this.adapter.onConnectionStateChange((state) => {
      const prev = this.connState;
      this.connState = state;

      if (state === "connected" && prev !== "connected") {
        this._registerTranscription();
      } else if (state !== "connected" && this.transcriptionRegistered) {
        this._unregisterTranscription();
      }
    });

    this.cleanupFns.push(unsub);

    this.connState = this.adapter.getConnectionState();
    if (this.connState === "connected") {
      this._registerTranscription();
    }
  }

  hostDisconnected(): void {
    this._unregisterTranscription();
    this.cleanupFns.forEach((fn) => fn());
    this.cleanupFns = [];
  }

  clearTranscripts(): void {
    this.segmentMap.clear();
    this.transcripts = [];
    this.host.requestUpdate();
  }

  private _registerTranscription(): void {
    this._unregisterTranscription();

    const unsub = this.adapter.onTranscription((segment: TranscriptionSegment) => {
      const existing = this.segmentMap.get(segment.id);

      const msg: ChatMessage = {
        name: segment.isAgent ? "Agent" : "You",
        message: segment.isFinal ? segment.text : `${segment.text} ...`,
        isSelf: !segment.isAgent,
        timestamp: existing?.timestamp ?? Date.now(),
      };

      this.segmentMap.set(segment.id, msg);

      this.transcripts = Array.from(this.segmentMap.values()).sort((a, b) => a.timestamp - b.timestamp);
      this.host.requestUpdate();
    });

    this.cleanupFns.push(unsub);
    this.transcriptionRegistered = true;
  }

  private _unregisterTranscription(): void {
    // Transcription is unregistered via the unsub function pushed to cleanupFns
    this.transcriptionRegistered = false;
  }
}
